/**
 * Intent Filter Config Service
 * Per-intent filtering toggle for the caller's account (JWT-scoped),
 * plus the account-wide intent-filtering enable/disable flag.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';

export interface AccountIntentFilterConfig {
  _id: string;
  accountId: string;
  intentId: string;
  isFilteringEnabled: boolean;
  bypassTagging: boolean;
  createdAt: string;
  updatedAt: string;
}

export const intentFilterConfigService = {
  async getEnabledConfigs(scope?: { bu_id?: string }): Promise<AccountIntentFilterConfig[]> {
    const response = await api.get<ApiResponse<{ configs: AccountIntentFilterConfig[]; count: number }>>(
      API_ENDPOINTS.ACCOUNT_INTENT_FILTER_CONFIGS.ENABLED,
      { params: scope }
    );
    return response.data.data.configs || [];
  },

  async upsertConfig(
    intentId: string,
    isFilteringEnabled: boolean,
    scope?: { bu_id?: string }
  ): Promise<AccountIntentFilterConfig> {
    const response = await api.patch<ApiResponse<{ config: AccountIntentFilterConfig }>>(
      withBuScope(API_ENDPOINTS.ACCOUNT_INTENT_FILTER_CONFIGS.BASE, scope ?? {}),
      { intentId, isFilteringEnabled }
    );
    return response.data.data.config;
  },

  /** "All intents" means all of THIS brand's intents when a brand is selected. */
  async setFilteringForAll(
    enabled: boolean,
    scope?: { bu_id?: string }
  ): Promise<{ updated: number; enabled: boolean }> {
    const response = await api.patch<ApiResponse<{ updated: number; enabled: boolean }>>(
      withBuScope(`${API_ENDPOINTS.ACCOUNT_INTENT_FILTER_CONFIGS.BASE}/bulk`, scope ?? {}),
      { enabled }
    );
    return response.data.data;
  },

  /**
   * Enable or disable intent-based filtering, for the selected brand or the account.
   *
   * This was the one carve-out that has since closed. The switch lives in `accounts.flag`,
   * which had no brand dimension — a brand's value now nests inside it the same way the
   * settings in `custom_attributes` do, and shadows the account's. A brand that has set
   * none still inherits.
   *
   * It matters because the switch is a GATE: with it off for a brand, none of that brand's
   * per-intent toggles do anything. Leaving it account-wide while the toggles beside it
   * were per-brand made those toggles look broken.
   */
  async updateFilteringEnabled(
    enabled: boolean,
    scope?: { bu_id?: string }
  ): Promise<{ flag: Record<string, unknown> }> {
    const response = await api.patch<ApiResponse<{ flag: Record<string, unknown> }>>(
      withBuScope(API_ENDPOINTS.ACCOUNTS.INTENT_FILTERING_ENABLED, scope ?? {}),
      { enabled }
    );
    return response.data.data;
  },

  /**
   * Intents where Ticket Preprocessing's tag add/remove is bypassed, independent of
   * `isFilteringEnabled` — a bypassed intent skips tagging even while intent filtering
   * itself is off or allows the intent through.
   */
  async getBypassTaggingConfigs(scope?: { bu_id?: string }): Promise<AccountIntentFilterConfig[]> {
    const response = await api.get<ApiResponse<{ configs: AccountIntentFilterConfig[]; count: number }>>(
      API_ENDPOINTS.ACCOUNT_INTENT_FILTER_CONFIGS.BYPASS_TAGGING,
      { params: scope }
    );
    return response.data.data.configs || [];
  },

  async setBypassTagging(
    intentId: string,
    bypassTagging: boolean,
    scope?: { bu_id?: string }
  ): Promise<AccountIntentFilterConfig> {
    const response = await api.patch<ApiResponse<{ config: AccountIntentFilterConfig }>>(
      withBuScope(API_ENDPOINTS.ACCOUNT_INTENT_FILTER_CONFIGS.BYPASS_TAGGING, scope ?? {}),
      { intentId, bypassTagging }
    );
    return response.data.data.config;
  }
};
